import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useIsAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setIsAdmin(false); setLoading(false); return; }
        const { data } = await supabase.rpc('has_role', { _user_id: session.user.id, _role: 'admin' });
        if (!cancelled) setIsAdmin(!!data);
      } catch { if (!cancelled) setIsAdmin(false); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return { isAdmin, loading };
}

export interface AdminUser {
  user_id: string;
  display_name: string | null;
  email: string | null;
  subscription_tier: string;
  stripe_customer_id: string | null;
  created_at: string;
}

export function useAdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await supabase.functions.invoke('admin-update-tier', { body: { action: 'list' } });
      if (res.error) throw res.error;
      setUsers(res.data?.users ?? []);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTier = useCallback(async (targetUserId: string, tier: string) => {
    const { error } = await supabase.functions.invoke('admin-update-tier', {
      body: { action: 'update-tier', target_user_id: targetUserId, tier },
    });
    if (error) throw error;
    setUsers(prev => prev.map(u => u.user_id === targetUserId ? { ...u, subscription_tier: tier } : u));
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  return { users, loading, error, refetch: fetchUsers, updateTier };
}
